import type {
  CoachingActionStatus,
  CoachingAlignmentPractice,
  CoachingMindsetPractice,
} from './constants';

/**
 * The Success Score (blueprint Part III §6): a 100-point measure of behaviours
 * completed INSIDE the app on one member-local day. It never implies fate or
 * the likelihood of any outcome, and no component reads astrology.
 *
 *   Action completion  35  primary (#1) action 25 + supporting actions 10
 *                          (35 for the primary alone when there is nothing
 *                          else scheduled); a partial counts half
 *   Morning ritual     15  all steps; a started ritual earns its share
 *   Mindset practice   15  3 per completion, at most 2 of each kind, max 5
 *   Alignment practice 10  per distinct alignment practice up to the target
 *   Evening review     15
 *   Goal progress      10  any goal progress update or milestone completed
 *
 * Pure: the API gathers the inputs, the SPA only renders the result.
 */

export const SUCCESS_SCORE_MAX = {
  actions: 35,
  morningRitual: 15,
  mindset: 15,
  alignment: 10,
  eveningReview: 15,
  goalProgress: 10,
} as const;

export type SuccessScoreComponent = keyof typeof SUCCESS_SCORE_MAX;

/**
 * Distinct alignment practices that earn the full 10. It is ONE, so adding a
 * kind to `COACHING_ALIGNMENT_PRACTICES` gives a member another way to earn the
 * same ten points and never raises what a day is worth. Historical scores stay
 * comparable; raising this number would break that.
 */
export const COACHING_ALIGNMENT_TARGET = 1;
/** Mindset completions that earn the full 15, and the per-kind cap. */
export const COACHING_MINDSET_TARGET = 5;
export const COACHING_MINDSET_PER_KIND_CAP = 2;

export type SuccessScoreInputs = {
  /** The day's scheduled actions. The lowest priority number is the #1 action. */
  actions: ReadonlyArray<{ priority: number; status: CoachingActionStatus }>;
  morningRitual: { completed: boolean; stepsCompleted?: number; totalSteps?: number };
  eveningReviewCompleted: boolean;
  mindsetCounts: Partial<Record<CoachingMindsetPractice, number>>;
  alignmentDone: ReadonlyArray<CoachingAlignmentPractice>;
  goalProgressUpdated: boolean;
};

export type SuccessScore = {
  total: number;
  breakdown: Record<SuccessScoreComponent, number>;
  label: string;
  /** The first component still below its maximum, in the order that raises the score fastest. */
  nextStep: SuccessScoreComponent | null;
};

const credit = (status: CoachingActionStatus): number =>
  status === 'done' ? 1 : status === 'partial' ? 0.5 : 0;

export function scoreLabel(total: number): string {
  if (total >= 80) return 'Strong day';
  if (total >= 60) return 'Good progress';
  if (total >= 30) return 'Building';
  return total > 0 ? 'Getting started' : 'Not started';
}

export function computeSuccessScore(input: SuccessScoreInputs): SuccessScore {
  // Stable sort: equal priorities keep the caller's order (created first wins).
  const ordered = [...input.actions].sort((a, b) => a.priority - b.priority);
  const [primary, ...rest] = ordered;
  const supporting = rest.slice(0, 2);

  let actions = 0;
  if (primary) {
    const primaryMax = supporting.length > 0 ? 25 : SUCCESS_SCORE_MAX.actions;
    actions += Math.floor(primaryMax * credit(primary.status));
    if (supporting.length > 0) {
      const earned = supporting.reduce((sum, a) => sum + credit(a.status), 0);
      actions += Math.floor((10 * earned) / supporting.length);
    }
  }

  let morningRitual = 0;
  if (input.morningRitual.completed) {
    morningRitual = SUCCESS_SCORE_MAX.morningRitual;
  } else if (input.morningRitual.totalSteps && input.morningRitual.stepsCompleted) {
    const share =
      Math.min(input.morningRitual.stepsCompleted, input.morningRitual.totalSteps) /
      input.morningRitual.totalSteps;
    morningRitual = Math.floor(SUCCESS_SCORE_MAX.morningRitual * share);
  }

  const mindsetCompletions = Object.values(input.mindsetCounts).reduce<number>(
    (sum, n) => sum + Math.max(0, Math.min(n ?? 0, COACHING_MINDSET_PER_KIND_CAP)),
    0,
  );
  const mindset =
    (SUCCESS_SCORE_MAX.mindset / COACHING_MINDSET_TARGET) *
    Math.min(mindsetCompletions, COACHING_MINDSET_TARGET);

  const distinctAlignment = new Set(input.alignmentDone).size;
  const alignment = Math.floor(
    (SUCCESS_SCORE_MAX.alignment * Math.min(distinctAlignment, COACHING_ALIGNMENT_TARGET)) /
      COACHING_ALIGNMENT_TARGET,
  );

  const breakdown: Record<SuccessScoreComponent, number> = {
    actions,
    morningRitual,
    mindset,
    alignment,
    eveningReview: input.eveningReviewCompleted ? SUCCESS_SCORE_MAX.eveningReview : 0,
    goalProgress: input.goalProgressUpdated ? SUCCESS_SCORE_MAX.goalProgress : 0,
  };
  const total = Object.values(breakdown).reduce((sum, n) => sum + n, 0);

  const order: SuccessScoreComponent[] = [
    'actions',
    'morningRitual',
    'eveningReview',
    'mindset',
    'alignment',
    'goalProgress',
  ];
  const nextStep = order.find((c) => breakdown[c] < SUCCESS_SCORE_MAX[c]) ?? null;

  return { total, breakdown, label: scoreLabel(total), nextStep };
}
